import { Component, type ReactNode } from "react";

interface Props { children: ReactNode; }
interface State { error: Error | null; componentStack: string | null; }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, componentStack: null };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    this.setState({ componentStack: info.componentStack });
  }

  render() {
    if (this.state.error) {
      // Extract the first few component names from the stack
      const stackLines = (this.state.componentStack ?? "")
        .split("\n")
        .map(l => l.trim())
        .filter(l => l.startsWith("at "))
        .slice(0, 4)
        .map(l => l.replace("at ", ""))
        .join(" → ");

      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
          <p className="text-lg font-semibold text-destructive">Something went wrong</p>
          <p className="text-sm text-muted-foreground max-w-md">{this.state.error.message}</p>
          {stackLines && (
            <p className="text-xs text-muted-foreground/70 max-w-lg font-mono bg-muted px-3 py-2 rounded">
              {stackLines}
            </p>
          )}
          <button
            className="px-4 py-2 rounded bg-primary text-primary-foreground text-sm hover:bg-primary/90"
            onClick={() => { this.setState({ error: null, componentStack: null }); window.location.reload(); }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
